import { api } from "lib/api";

/** Prefer smaller Strapi formats for list/cart tiles; originals for detail heroes. */
const SIZE_CHAINS = {
  thumbnail: ["thumbnail", "small", "medium", "large", "url"],
  small: ["small", "medium", "thumbnail", "large", "url"],
  medium: ["medium", "large", "small", "url"],
  large: ["large", "medium", "url"],
  original: ["url", "large", "medium", "small", "thumbnail"],
};

function pickFormatUrl(file, sizeKey) {
  if (!file || sizeKey === "url") {
    return file?.url || file?.attributes?.url || null;
  }
  return (
    file?.formats?.[sizeKey]?.url ||
    file?.attributes?.formats?.[sizeKey]?.url ||
    null
  );
}

function toAbsoluteMediaUrl(candidate) {
  const value = String(candidate || "").trim();
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  const base = String(api?.defaults?.baseURL || "").replace(/\/+$/, "");
  return base ? `${base}${value.startsWith("/") ? value : `/${value}`}` : value;
}

/**
 * Resolve a Strapi media file to an absolute URL.
 * @param {object|null} file
 * @param {{ size?: keyof typeof SIZE_CHAINS }} [options]
 *   - original (default): full upload URL when available
 *   - thumbnail | small | medium | large: prefer matching formats.* then fall back
 */
export function mediaUrl(file, options = {}) {
  if (!file) return null;
  const size = SIZE_CHAINS[options?.size] ? options.size : "original";
  const chain = SIZE_CHAINS[size];
  for (const key of chain) {
    const candidate = pickFormatUrl(file, key);
    if (candidate) return toAbsoluteMediaUrl(candidate);
  }
  return null;
}

/** First media file from a Strapi media field (array / data / files / single). */
export function firstMediaFile(media) {
  if (!media) return null;
  if (Array.isArray(media)) return media[0] || null;
  if (Array.isArray(media?.data)) return media.data[0] || null;
  if (Array.isArray(media?.files)) return media.files[0] || null;
  return media;
}

export function firstMediaUrl(media, options = {}) {
  return mediaUrl(firstMediaFile(media), options);
}

/** ~156px tiles / cart / gallery strips */
export function thumbnailMediaUrl(file) {
  return mediaUrl(file, { size: "thumbnail" });
}

export function firstThumbnailMediaUrl(media) {
  return firstMediaUrl(media, { size: "thumbnail" });
}

/** Catalog grid / home rails (~200–400px display) */
export function listMediaUrl(file) {
  return mediaUrl(file, { size: "small" });
}

export function firstListMediaUrl(media) {
  return firstMediaUrl(media, { size: "small" });
}
